// src/components/cards/shared/dialogs/convert/ConvertClientSection.tsx
//
// Client-details section for the prospect convert flow. Uses the same
// FormField primitives as ClientForm for consistent styling/validation.
// Required: firstName, lastName, phone. Email is optional but
// format-validated when supplied.

import React from 'react';
import { Box } from '@mui/material';
import {
  FormFieldRow,
  FieldLabelContainer,
  FieldLabel,
  FieldInputContainer,
  StyledTextField,
} from '../../../../forms/shared';
import CustomSelect from '../../../../common/CustomSelect';
import CustomSelectMenuItem from '../../../../common/CustomSelectMenuItem';
import { Vet } from '../../../../../services/api';

export interface ConvertClientFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  vetId: string;
  idNumber: string;
  alternateContact: string;
  alternateContactOther: string;
}

export interface ConvertClientFormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

interface ConvertClientSectionProps {
  value: ConvertClientFormState;
  errors: ConvertClientFormErrors;
  availableVets: Vet[];
  isSubmitting: boolean;
  onChange: (next: ConvertClientFormState) => void;
  // DOM id for scroll-to-first-error.
  id?: string;
}

const ConvertClientSection: React.FC<ConvertClientSectionProps> = ({
  value,
  errors,
  availableVets,
  isSubmitting,
  onChange,
  id,
}) => {
  const uid = React.useId();

  const update = <K extends keyof ConvertClientFormState>(
    key: K,
    next: ConvertClientFormState[K]
  ) => {
    onChange({ ...value, [key]: next });
  };

  return (
    <Box id={id}>
      {/* First Name * */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel id={`${uid}-firstName-label`}>
            First Name
            <span className="required" aria-hidden="true">
              *
            </span>
          </FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <StyledTextField
            fullWidth
            inputProps={{ 'aria-labelledby': `${uid}-firstName-label` }}
            value={value.firstName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              update('firstName', e.target.value)
            }
            error={Boolean(errors.firstName)}
            helperText={errors.firstName}
            disabled={isSubmitting}
          />
        </FieldInputContainer>
      </FormFieldRow>

      {/* Last Name * */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel id={`${uid}-lastName-label`}>
            Last Name
            <span className="required" aria-hidden="true">
              *
            </span>
          </FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <StyledTextField
            fullWidth
            inputProps={{ 'aria-labelledby': `${uid}-lastName-label` }}
            value={value.lastName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              update('lastName', e.target.value)
            }
            error={Boolean(errors.lastName)}
            helperText={errors.lastName}
            disabled={isSubmitting}
          />
        </FieldInputContainer>
      </FormFieldRow>

      {/* Email (optional, format-validated) */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel id={`${uid}-email-label`}>Email</FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <StyledTextField
            fullWidth
            type="email"
            inputProps={{ 'aria-labelledby': `${uid}-email-label` }}
            value={value.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              update('email', e.target.value)
            }
            error={Boolean(errors.email)}
            helperText={errors.email}
            disabled={isSubmitting}
          />
        </FieldInputContainer>
      </FormFieldRow>

      {/* Phone * */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel id={`${uid}-phone-label`}>
            Phone
            <span className="required" aria-hidden="true">
              *
            </span>
          </FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <StyledTextField
            fullWidth
            inputProps={{ 'aria-labelledby': `${uid}-phone-label` }}
            value={value.phone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              update('phone', e.target.value)
            }
            error={Boolean(errors.phone)}
            helperText={errors.phone}
            disabled={isSubmitting}
          />
        </FieldInputContainer>
      </FormFieldRow>

      {/* Address */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel id={`${uid}-address-label`}>Address</FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <StyledTextField
            fullWidth
            inputProps={{ 'aria-labelledby': `${uid}-address-label` }}
            value={value.address}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              update('address', e.target.value)
            }
            disabled={isSubmitting}
          />
        </FieldInputContainer>
      </FormFieldRow>

      {/* Vet (select, default "None") */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel>Vet</FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <CustomSelect
            value={value.vetId}
            onChange={(next) => update('vetId', next)}
            ariaLabel="Vet"
            label=""
            placeholder="None"
            disabled={isSubmitting}
            width="100%"
            maxWidth="600px"
          >
            <CustomSelectMenuItem value="">None</CustomSelectMenuItem>
            {availableVets.map((v) => (
              <CustomSelectMenuItem key={v.id} value={v.id.toString()}>
                {v.name}
              </CustomSelectMenuItem>
            ))}
          </CustomSelect>
        </FieldInputContainer>
      </FormFieldRow>

      {/* ID Number */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel id={`${uid}-idNumber-label`}>ID Number</FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <StyledTextField
            fullWidth
            inputProps={{ 'aria-labelledby': `${uid}-idNumber-label` }}
            value={value.idNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              update('idNumber', e.target.value)
            }
            disabled={isSubmitting}
          />
        </FieldInputContainer>
      </FormFieldRow>

      {/* Alt. Contact */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel id={`${uid}-alternateContact-label`}>
            Alt. Contact
          </FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <StyledTextField
            fullWidth
            inputProps={{ 'aria-labelledby': `${uid}-alternateContact-label` }}
            value={value.alternateContact}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              update('alternateContact', e.target.value)
            }
            disabled={isSubmitting}
          />
        </FieldInputContainer>
      </FormFieldRow>

      {/* Alt. Contact Other */}
      <FormFieldRow>
        <FieldLabelContainer>
          <FieldLabel id={`${uid}-alternateContactOther-label`}>
            Alt. Contact Other
          </FieldLabel>
        </FieldLabelContainer>
        <FieldInputContainer sx={{ paddingLeft: '1rem', maxWidth: '600px' }}>
          <StyledTextField
            fullWidth
            inputProps={{
              'aria-labelledby': `${uid}-alternateContactOther-label`,
            }}
            value={value.alternateContactOther}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              update('alternateContactOther', e.target.value)
            }
            disabled={isSubmitting}
          />
        </FieldInputContainer>
      </FormFieldRow>
    </Box>
  );
};

export default ConvertClientSection;
